// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { NumberTextField } from '@/components/number-text-field';
import { Shipment, ShipmentItem } from '@/shipment/api/types';
import { ShipmentItemTypeIcon } from '@/shipment/components/shipment-item-type-icon';
import { useEditLineItems } from '@/shipment/hooks/use-edit-line-items';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { Link } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { ReactNode, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipment: Shipment;
  item: ShipmentItem;
}

// Error code for insufficient inventory
const INSUFFICIENT_INVENTORY_ERROR_CODE = 2255;
// Error code for automatic correction failure
const AUTOMATIC_CORRECTION_FAILED_ERROR_CODE = 2209;

/**
 * Parses the available quantity from an error message like "insufficient inventory at destination: 5 available"
 */
function parseAvailableQuantity(message: string): number | null {
  const match = message.match(/(\d+)\s+available/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Dialog for correcting quantity errors on outbound shipment line items.
 * Allows users to adjust quantities that were recorded in error.
 */
export function CorrectQuantityDialog(props: Props) {
  const { isOpen, onClose, shipment, item: lineItem } = props;

  const itemName = lineItem.item.name ?? 'Unknown';
  const itemSku = lineItem.item.sku || null;
  const originalQuantity = lineItem.quantity ?? 0;

  const [revisedQuantity, setRevisedQuantity] = useState<number | null>(
    originalQuantity
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);

  const editLineItemsMutation = useEditLineItems(shipment.key);

  const handleClose = () => {
    setRevisedQuantity(originalQuantity);
    setErrorMessage(null);
    onClose();
  };

  const handleAttemptCorrection = async () => {
    if (revisedQuantity === null || revisedQuantity === originalQuantity) {
      handleClose();
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    try {
      await editLineItemsMutation.mutateAsync({
        items: [
          {
            itemId: lineItem.id,
            newQuantity: revisedQuantity,
          },
        ],
      });
      handleClose();
    } catch (error) {
      const apiError = handleAPIError(error as AxiosError);
      const curated = getUserFacingErrorMessage(apiError);

      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
      } else if (apiError.apiStatusCode === INSUFFICIENT_INVENTORY_ERROR_CODE) {
        // Parse the available quantity from the error message
        const availableQty = parseAvailableQuantity(apiError.message);
        const isDecrease = revisedQuantity < originalQuantity;

        if (isDecrease) {
          // Decrease correction - not enough at destination
          const locationName = shipment.toLocation?.name ?? 'destination';
          if (availableQty !== null) {
            setErrorMessage(
              `There are only ${formatNumber(availableQty)} units available at ${locationName}, which is not enough to complete this correction by shipping units back.`
            );
          } else {
            setErrorMessage(
              `There is not enough inventory available at ${locationName} to complete this correction by shipping units back.`
            );
          }
        } else {
          // Increase correction - not enough at origin
          const locationName = shipment.fromLocation?.name ?? 'origin';
          if (availableQty !== null) {
            setErrorMessage(
              `There are only ${formatNumber(availableQty)} units available at ${locationName}, which is not enough to complete this correction by shipping out more units.`
            );
          } else {
            setErrorMessage(
              `There is not enough inventory available at ${locationName} to complete this correction by shipping out more units.`
            );
          }
        }
      } else if (
        apiError.apiStatusCode === AUTOMATIC_CORRECTION_FAILED_ERROR_CODE
      ) {
        const itemLabel = itemSku ? `[${itemSku}]` : itemName;
        const itemLink = lineItem.item.key ? (
          <Link
            to="/catalog/items/$itemKey"
            params={{ itemKey: lineItem.item.key }}
            style={{ color: 'inherit' }}
          >
            {itemLabel}
          </Link>
        ) : (
          itemLabel
        );
        setErrorMessage(
          <>
            Hardfin could not update the shipment data automatically for this
            line item. Please transfer or adjust inventory for {itemLink} to
            correct the error manually.
          </>
        );
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage('An error occurred while attempting the correction.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = revisedQuantity !== originalQuantity;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Correct shipment quantity error</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">
            If this quantity was recorded in error, you can attempt to update
            the quantity. Hardfin will try to automatically correct the quantity
            if possible.
          </Typography>

          <Box component="ul" sx={{ m: 0 }}>
            <Typography component="li" variant="body2">
              If you need to decrease the quantity, Hardfin will try to move
              units back.
            </Typography>
            <Typography component="li" variant="body2">
              If you need to increase the quantity, Hardfin will try to move
              more units.
            </Typography>
          </Box>

          <Alert severity="info">
            If there is insufficient quantity available, Hardfin will be unable
            to automatically correct the error and you will have to resolve it
            manually.
          </Alert>

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <ShipmentItemTypeIcon item={lineItem} />
              <Typography variant="subtitle2">
                {itemSku ?? itemName}{' '}
                {itemSku && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    ({itemName})
                  </Typography>
                )}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Original quantity (received in error)
                </Typography>
                <NumberTextField
                  value={originalQuantity}
                  onChange={() => {
                    // Read-only field, no-op
                  }}
                  disabled
                  size="small"
                  fullWidth
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Revised quantity (to attempt correction)
                </Typography>
                <NumberTextField
                  value={revisedQuantity}
                  onChange={(values) =>
                    setRevisedQuantity(values.floatValue ?? null)
                  }
                  allowZero
                  allowNegative={false}
                  maximumFractionDigits={0}
                  size="small"
                  fullWidth
                />
              </Box>
            </Stack>
          </Box>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAttemptCorrection}
          disabled={isSaving || !hasChanges}
        >
          Attempt correction
        </Button>
      </DialogActions>
    </Dialog>
  );
}
